import {
  AdminStoreListItem,
  GooglePlaceDetails,
  Route,
  UpdateStoreAdmin,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { request } from "../api"

async function fetchAdminStores(): Promise<AdminStoreListItem[]> {
  return request<AdminStoreListItem[]>({
    url: Route.STORE__ADMIN_FIND_ALL,
    method: "GET",
  })
}

async function createStore(formData: FormData): Promise<string> {
  return request<string>({
    url: Route.STORE_CONTROLLER__CREATE,
    method: "POST",
    data: formData,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })
}

export const adminStoreQueries = {
  all: () => ["admin", "stores"],
  list: () =>
    queryOptions({
      queryKey: adminStoreQueries.all(),
      queryFn: () => fetchAdminStores(),
      staleTime: Number.POSITIVE_INFINITY,
    }),
}

async function updateStoreTier(
  storeId: string,
  tier: string | null,
): Promise<void> {
  return request<void>({
    url: populateRoute(Route.STORE__ADMIN_UPDATE_TIER, storeId),
    method: "PATCH",
    data: { tier },
  })
}

export const useUpdateStoreTierMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ storeId, tier }: { storeId: string; tier: string | null }) =>
      updateStoreTier(storeId, tier),
    onMutate: ({ storeId, tier }) => {
      queryClient.setQueryData<AdminStoreListItem[]>(
        adminStoreQueries.all(),
        (old) =>
          old?.map((store) =>
            store.id === storeId ? { ...store, tier } : store,
          ),
      )
    },
  })
}

async function updateStoreAdmin(
  storeId: string,
  update: UpdateStoreAdmin,
): Promise<void> {
  return request<void>({
    url: populateRoute(Route.STORE__ADMIN_UPDATE, storeId),
    method: "PATCH",
    data: update,
  })
}

export const useUpdateStoreAdminMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      storeId,
      update,
    }: {
      storeId: string
      update: UpdateStoreAdmin
    }) => updateStoreAdmin(storeId, update),
    onMutate: ({ storeId, update }) => {
      queryClient.setQueryData<AdminStoreListItem[]>(
        adminStoreQueries.all(),
        (old) =>
          old?.map((store) =>
            store.id === storeId
              ? {
                  ...store,
                  email: update.email ?? store.email,
                  slug: update.slug ?? store.slug,
                  hiddenOnJulo: update.hiddenOnJulo ?? store.hiddenOnJulo,
                  stripePayoutsEnabled:
                    update.stripePayoutsEnabled ?? store.stripePayoutsEnabled,
                  whiteLabelBookingEnabled:
                    update.whiteLabelBookingEnabled ??
                    store.whiteLabelBookingEnabled,
                  companiesEnabled:
                    update.companiesEnabled ?? store.companiesEnabled,
                  customerClubEnabled:
                    update.customerClubEnabled ?? store.customerClubEnabled,
                  testMode: update.testMode ?? store.testMode,
                }
              : store,
          ),
      )
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: adminStoreQueries.all() })
    },
  })
}

async function fetchGooglePlaceDetails(
  googlePlaceId: string,
): Promise<GooglePlaceDetails | null> {
  return request<GooglePlaceDetails | null>({
    url: populateRoute(
      Route.STORE__ADMIN_FETCH_GOOGLE_PLACE_DETAILS,
      googlePlaceId,
    ),
    method: "GET",
  })
}

export const useFetchGooglePlaceDetailsMutation = () => {
  return useMutation({
    mutationFn: (googlePlaceId: string) =>
      fetchGooglePlaceDetails(googlePlaceId),
  })
}

export const useCreateStoreMutation = () => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (formData: FormData) => createStore(formData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminStoreQueries.all(),
      })
      navigate({ to: "/" })
    },
  })
}

export interface CheckEmailVerifiedResult {
  email: string
  isAlfredUser: boolean
  isCyclistAccount: boolean
  existsInFirebase: boolean
  emailVerified: boolean | null
}

export interface UserEmailVerifiedItem {
  email: string
  isCyclistAccount: boolean
  existsInFirebase: boolean
  emailVerified: boolean | null
}

export interface SetAllEmailVerifiedResult {
  updated: number
  skipped: number
  notFound: number
  total: number
}

export const useCheckEmailVerifiedMutation = () =>
  useMutation({
    mutationFn: (email: string) =>
      request<CheckEmailVerifiedResult>({
        url: populateRoute(Route.ADMIN_CONTROLLER__CHECK_EMAIL_VERIFIED, email),
        method: "GET",
      }),
  })

export const useListUsersEmailVerifiedMutation = () =>
  useMutation({
    mutationFn: () =>
      request<UserEmailVerifiedItem[]>({
        url: Route.ADMIN_CONTROLLER__LIST_USERS_EMAIL_VERIFIED,
        method: "GET",
      }),
  })

export const useSetAllEmailVerifiedMutation = () =>
  useMutation({
    mutationFn: () =>
      request<SetAllEmailVerifiedResult>({
        url: Route.ADMIN_CONTROLLER__SET_ALL_EMAIL_VERIFIED,
        method: "POST",
      }),
  })
